import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFirestoreDoc } from "../firebase/listeners.js";
import type { CustomizationRecord } from "../lib/customization-edits.js";
import CustomizationExtrasEditor from "./CustomizationExtrasEditor.js";
import CustomizationSkillsEditor from "./CustomizationSkillsEditor.js";
import CustomizationStuntsEditor from "./CustomizationStuntsEditor.js";

interface Props {
  open: boolean;
  tableId: string;
  packSkillIds: string[];
  onClose: () => void;
}

const EMPTY: CustomizationRecord = {
  skills: { added: [], removed: [], renamed: {} },
  stunts: { addedLibrary: [], removedLibraryIds: [] },
  extras: { addedLibrary: [], removedLibraryIds: [] },
};

export default function CustomizationPanel({
  open,
  tableId,
  packSkillIds,
  onClose,
}: Props): ReactElement | null {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"skills" | "stunts" | "extras">("skills");
  const { data: tableDoc } = useFirestoreDoc<{ customization?: CustomizationRecord }>(
    open ? `tables/${tableId}` : null,
  );
  if (!open) return null;
  const customization = tableDoc?.customization ?? EMPTY;
  const tablePath = `/tables/${tableId}`;

  return (
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative showModal(); ARIA dialog pattern is intentional
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="customization-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto dialog-surface rounded-lg p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 id="customization-title" className="text-lg font-semibold">
            {t("customization.title")}
          </h2>
          <button type="button" className="rounded border px-2 py-0.5 text-xs" onClick={onClose}>
            {t("edit.cancel")}
          </button>
        </div>

        <div role="tablist" className="flex gap-2 border-b">
          {(["skills", "stunts", "extras"] as const).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`px-3 py-1 text-sm ${tab === key ? "border-b-2 border-emerald-600 font-medium" : ""}`}
              onClick={() => setTab(key)}
            >
              {t(`customization.tab.${key}`)}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "skills" && (
            <CustomizationSkillsEditor
              tablePath={tablePath}
              customization={customization}
              packSkillIds={packSkillIds}
            />
          )}
          {tab === "stunts" && (
            <CustomizationStuntsEditor tablePath={tablePath} customization={customization} />
          )}
          {tab === "extras" && (
            <CustomizationExtrasEditor tablePath={tablePath} customization={customization} />
          )}
        </div>
      </div>
    </div>
  );
}
